"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CONTENT_TRANSLATION_STATUS_OPTIONS = exports.ContentTranslationEntrySchema = exports.PublishedContentTranslationStatusSchema = exports.ContentTranslationStatusSchema = exports.ContentI18nRowStateSchema = exports.CONTENT_I18N_ROW_STATE_KEY = exports.DEFAULT_CONTENT_I18N_POLICY = exports.ContentI18nPolicySchema = exports.ContentI18nMissingTranslationPolicySchema = void 0;
exports.parseContentI18nRowState = parseContentI18nRowState;
exports.parseContentTranslationRows = parseContentTranslationRows;
exports.contentI18nOptionField = contentI18nOptionField;
exports.parseContentI18nOptionField = parseContentI18nOptionField;
exports.isPublishedContentTranslation = isPublishedContentTranslation;
exports.isContentI18nTranslatableColumn = isContentI18nTranslatableColumn;
const database_identity_1 = require("@streamlain/database-identity");
const zod_1 = require("zod");
exports.ContentI18nMissingTranslationPolicySchema = zod_1.z.enum([
    'default-locale-fallback',
    'strict',
]);
exports.ContentI18nPolicySchema = zod_1.z.object({
    missingTranslation: exports.ContentI18nMissingTranslationPolicySchema,
});
exports.DEFAULT_CONTENT_I18N_POLICY = {
    missingTranslation: 'default-locale-fallback',
};
exports.CONTENT_I18N_ROW_STATE_KEY = '__contentI18n';
exports.ContentI18nRowStateSchema = zod_1.z.object({
    state: zod_1.z.enum(['translated', 'fallback']),
    requestedLocale: zod_1.z.string().min(2),
    resolvedLocale: zod_1.z.string().min(2),
    defaultLocale: zod_1.z.string().min(2),
    missingFields: zod_1.z.array(zod_1.z.string()),
});
function parseContentI18nRowState(value) {
    const parsed = exports.ContentI18nRowStateSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
exports.ContentTranslationStatusSchema = zod_1.z.enum([
    'draft',
    'pending',
    'approved',
    'published',
    'rejected',
]);
exports.PublishedContentTranslationStatusSchema = zod_1.z.enum([
    'approved',
    'published',
]);
exports.ContentTranslationEntrySchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    entityDb: zod_1.z.string().min(1),
    entityRowId: zod_1.z.string().min(1),
    field: zod_1.z.string().min(1),
    locale: zod_1.z.string().min(2),
    value: zod_1.z.string(),
    status: exports.ContentTranslationStatusSchema,
    sourceHash: zod_1.z.string().min(1),
    updatedAt: zod_1.z.string().min(1),
});
/** The status options of a translations table, one per ContentTranslationStatus. */
exports.CONTENT_TRANSLATION_STATUS_OPTIONS = [
    { id: 'draft', value: 'draft', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Yellow },
    { id: 'pending', value: 'pending', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Blue },
    {
        id: 'approved',
        value: 'approved',
        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Purple,
    },
    {
        id: 'published',
        value: 'published',
        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Green,
    },
    { id: 'rejected', value: 'rejected', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Red },
];
const REQUIRED_FIELDS = [
    'entityDb',
    'entityRowId',
    'field',
    'locale',
    'value',
    'status',
    'sourceHash',
    'updatedAt',
];
function parseContentTranslationRows(rows, columns) {
    const columnIdByName = new Map(columns.map(column => [column.name, column.id]));
    return rows.map(row => {
        const raw = { id: row.id };
        for (const field of REQUIRED_FIELDS) {
            raw[field] = readTranslationCell(row.cells, columnIdByName, field);
        }
        return exports.ContentTranslationEntrySchema.parse(raw);
    });
}
const CONTENT_I18N_OPTION_FIELD_PREFIX = 'option:';
/**
 * Field of a translation entry that names the options of a select or
 * multi-select column; its `entityRowId` is the option ID.
 */
function contentI18nOptionField(columnId) {
    return `${CONTENT_I18N_OPTION_FIELD_PREFIX}${columnId}`;
}
/** The column ID a translation entry's field names options of, else null. */
function parseContentI18nOptionField(field) {
    return field.startsWith(CONTENT_I18N_OPTION_FIELD_PREFIX)
        ? field.slice(CONTENT_I18N_OPTION_FIELD_PREFIX.length) || null
        : null;
}
function isPublishedContentTranslation(entry) {
    return exports.PublishedContentTranslationStatusSchema.safeParse(entry.status)
        .success;
}
function isContentI18nTranslatableColumn(column) {
    const name = column.name.trim().toLowerCase();
    return (['title', 'text', 'rich-text'].includes(column.type) &&
        !name.startsWith('_') &&
        // "Published at" and "published_at" name the same field as "publishedAt".
        !CONTENT_I18N_NON_TRANSLATABLE_FIELD_NAMES.has(name.replace(/[\s_-]+/g, '')) &&
        !name.endsWith('id') &&
        !name.endsWith('ids'));
}
const CONTENT_I18N_NON_TRANSLATABLE_FIELD_NAMES = new Set([
    'status',
    'visibility',
    'audience',
    'published',
    'publishedat',
    'scheduledat',
    'updatedat',
    'createdat',
    'slug',
    'path',
    'canonical',
    'hreflanggroup',
    'locale',
    'ctaurl',
    'email',
    // The key a page looks a text up by, like "order.cartEmpty".
    'key',
]);
function readTranslationCell(cells, columnIdByName, field) {
    const columnId = columnIdByName.get(field);
    return columnId ? cells[columnId] : cells[field];
}
